import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import React__default from 'react';
import { Select } from 'ariakit/select';
import { themeGet } from '@styled-system/theme-get';
import styled, { css } from 'styled-components';
import { Box } from '../Box/index.js';
import { Flex } from '../Flex/index.js';
import { Icon } from '../Icon/index.js';
import { Text } from '../Text/index.js';
import { useInputGroupContext } from '../InputGroup/index.js';
import { selectStyles } from './styles.js';

/* ======================================= */
const StyledSelect = styled(Box).withConfig({
  shouldForwardProp: prop => {
    return !['inlineLabel'].includes(prop);
  }
}).withConfig({
  displayName: "SelectAlpha__StyledSelect",
  componentId: "sc-crdy17-0"
})(["&:disabled{pointer-events:auto !important;background-color:", ";border-color:", ";cursor:not-allowed;color:", ";&:hover{border-color:", ";}::placeholder{opacity:1;}}&[data-focus-visible],&[aria-expanded='true']{box-shadow:", ";&:hover{border-color:", ";}}", ""], themeGet('colors.white'), themeGet('colors.inputDisabled'), themeGet('colors.inputDisabled'), themeGet('colors.inputDisabled'), themeGet('shadows.focus'), themeGet('colors.inputActive'), _ref => {
  let {
    isInvalid
  } = _ref;
  return isInvalid && css(["border-color:", ";&[data-focus-visible],&[aria-expanded='true']{box-shadow:", ";&:hover{border-color:", ";}}&:hover:not(:disabled){border-color:", ";}"], themeGet('colors.inputAlert'), themeGet('shadows.error'), themeGet('colors.inputAlert'), themeGet('colors.inputAlert'));
});
/* ======================================= */
/**
 * Based on feedback from our developers, SelectAlpha presents several challenges,
 * and we recommend opting for Select for future implementations as we explore alternative solutions.
 *
 * ### Documentation
 * - [Examples](https://blockparty.endpoint.com/components/select-alpha)
 * <br />
 *  @deprecated Please use Select for future implementation
 */
const SelectAlpha = props => {
  const {
    isInvalid,
    isDisabled,
    disabled,
    placeholder,
    inlineLabel,
    state,
    children
  } = props;
  const {
    value
  } = state;
  const formControl = useInputGroupContext();
  const _isInvalid = isInvalid || formControl.isInvalid;
  const _isDisabled = isDisabled || disabled || formControl.isDisabled;
  // <select> does not have placeholder, but we are providing a placeholder
  // option to align with existing designs
  const hasPlaceHolder = state.value === '' && placeholder;
  let renderedChildren;
  if (children) {
    renderedChildren = children;
  } else {
    renderedChildren = /*#__PURE__*/React__default.createElement(Text, {
      color: _isDisabled ? 'inputDisabled' : hasPlaceHolder ? 'carbon500' : 'textPrimary',
      dataTestId: "select-input-text",
      size: {
        base: 'fontSize30',
        md: 'fontSize20'
      },
      textStyle: "truncate"
    }, hasPlaceHolder ? placeholder : value);
  }
  return /*#__PURE__*/React__default.createElement(StyledSelect
  // aria-labelledby={_id} // * Save for future consideration
  // @ts-ignore - conflicting `as` prop with styled-component
  , _extends({
    as: Select,
    backgroundColor: "white",
    disabled: _isDisabled,
    isInvalid: _isInvalid
  }, inlineLabel && {
    'aria-label': inlineLabel
  }, selectStyles, props), inlineLabel && /*#__PURE__*/React__default.createElement(Box, {
    as: "label",
    htmlFor: formControl?.groupId
  }, /*#__PURE__*/React__default.createElement(Text, {
    color: _isDisabled ? 'inputDisabled' : _isInvalid ? 'inputAlert' : 'carbon500',
    mr: "space30"
  }, inlineLabel, ":")), /*#__PURE__*/React__default.createElement(Flex, {
    justifyContent: "space-between",
    width: "100%"
  }, renderedChildren), /*#__PURE__*/React__default.createElement(SelectArrowAlpha, {
    isDisabled: _isDisabled
  }));
};
/* ======================================= */
const SelectArrowAlpha = _ref2 => {
  let {
    isDisabled
  } = _ref2;
  return /*#__PURE__*/React__default.createElement(Flex, {
    alignItems: "center",
    "aria-hidden": "true",
    height: "100%",
    pointerEvents: "none",
    position: "absolute"
    // matches figma value
    ,
    py: "6px",
    right: "4px"
  }, /*#__PURE__*/React__default.createElement(Box, {
    borderLeft: "1px solid",
    borderLeftColor: isDisabled ? 'disabled' : 'carbon300',
    height: "100%",
    mr: "space20"
  }), /*#__PURE__*/React__default.createElement(Icon, {
    color: isDisabled ? 'disabled' : 'carbon600',
    name: "ArrowChevronDown",
    size: "large"
  }));
};

export { SelectAlpha };
